import type { ControlState } from "../sim/controls";
import type { SimEvent } from "../sim/events";
import { hashWorld } from "../sim/hash";
import type { WorldState } from "../sim/state";
import { step } from "../sim/step";
import { MAX_INPUTS_PER_MESSAGE, type PlayerSlot, packControls, unpackControls } from "./protocol";

/** Tuning (04_NETCODE.md §3). Both players must use the same `inputDelay`. */
export interface RollbackOptions {
	/** Local inputs apply this many ticks later, which hides most network jitter. */
	inputDelay: number;
	/** Run at most this many ticks ahead of the remote player's inputs, then wait. */
	maxPrediction: number;
	/** Report a world hash for every tick that is a multiple of this, once it is final. */
	hashInterval: number;
	/** Re-check the time sync this often (ticks). */
	syncInterval: number;
}

export const DEFAULT_ROLLBACK: Readonly<RollbackOptions> = Object.freeze({
	inputDelay: 2,
	maxPrediction: 12,
	hashInterval: 60,
	syncInterval: 30,
});

/** Hashed states are kept this long (ticks): longer than the server takes to answer. */
const KEPT_HASHED_TICKS = 600;
/** Most ticks a time-sync correction waits at once (spread over the next sync intervals). */
const MAX_SYNC_WAIT = 8;

export interface RollbackHooks {
	/** Right before every step, forward or re-simulated (render interpolation capture). */
	beforeStep?(world: WorldState): void;
	/** After every step. `replay`: the tick was already presented and is being re-simulated. */
	afterStep?(events: SimEvent[], tick: number, replay: boolean): void;
	/** A rollback is about to restore an earlier state (the world is still the predicted one). */
	beforeRollback?(world: WorldState): void;
	/** A rollback finished: the world jumped from a predicted to a corrected state. */
	afterRollback?(fromTick: number, ticks: number): void;
}

export interface RollbackStats {
	rollbacks: number;
	/** Ticks re-simulated in total. */
	resimulated: number;
	longestRollback: number;
	/** Fixed steps spent waiting (prediction limit or time sync). */
	waits: number;
}

/**
 * GGPO-style rollback for a two-player match over the deterministic sim. The local player's
 * inputs apply after `inputDelay` ticks; the remote player's not-yet-known inputs are predicted
 * as "same as last known". When a real remote input differs from what was predicted, the world
 * is restored (in place, so references to it stay valid) to the snapshot before that tick and
 * re-simulated to the present. Pure: the caller moves inputs and hashes over the network.
 */
export class RollbackSession {
	/** Every input per player slot by tick; the local list runs `inputDelay` ahead. */
	private readonly inputs: number[][];
	/** The other players' slots (one in a duel, up to three in a party room, P11.3). */
	readonly remotes: readonly PlayerSlot[];
	/** The inputs each simulated tick actually used, per slot (real or predicted). */
	private readonly used: number[][] = [];
	/** World state before stepping tick t, for every tick that may still be rolled back. */
	private readonly snapshots = new Map<number, WorldState>();
	private readonly controls: ControlState[] = [];
	/** Earliest tick whose remote input turned out to be mispredicted, pending a rollback. */
	private mispredicted = Number.POSITIVE_INFINITY;
	private sent = 0;
	/** Each remote player's reported advantage (time sync). */
	private readonly advantages = new Map<PlayerSlot, number>();
	private syncWait = 0;
	/** Highest world tick ever presented (re-simulating up to here is a replay). */
	private presented: number;
	private hashedThrough = 0;
	private readonly pendingHashes: Array<{ tick: number; hash: string }> = [];
	/** The last few hashed states, for a desync report (P10.7). */
	private readonly hashedStates = new Map<number, WorldState>();
	readonly stats: RollbackStats = { rollbacks: 0, resimulated: 0, longestRollback: 0, waits: 0 };

	constructor(
		readonly world: WorldState,
		readonly local: PlayerSlot,
		readonly options: Readonly<RollbackOptions> = DEFAULT_ROLLBACK,
		private hooks: RollbackHooks = {},
	) {
		if (world.tick !== 0) throw new Error("a rollback session starts at tick 0");
		const players = Math.max(2, world.players.length);
		this.inputs = Array.from({ length: players }, () => []);
		this.remotes = Array.from({ length: players }, (_, i) => i as PlayerSlot).filter(
			(i) => i !== local,
		);
		for (let t = 0; t < options.inputDelay; t++) this.inputs[local]?.push(0);
		this.presented = 0;
	}

	/**
	 * Rejoining a running match: replays every player's logged inputs (from the server) on a
	 * fresh world, silently, up to where this player had already decided its inputs.
	 */
	static resume(
		world: WorldState,
		local: PlayerSlot,
		logs: readonly (readonly number[])[],
		options: Readonly<RollbackOptions> = DEFAULT_ROLLBACK,
		hooks: RollbackHooks = {},
	): RollbackSession {
		const session = new RollbackSession(world, local, options);
		const ownLog = session.inputs[local] ?? [];
		const own = logs[local] ?? [];
		if (own.length > options.inputDelay) {
			ownLog.length = 0;
			ownLog.push(...own);
		}
		for (const slot of session.remotes) session.inputs[slot]?.push(...(logs[slot] ?? []));
		session.sent = ownLog.length;
		const target = ownLog.length - options.inputDelay;
		for (let t = 0; t < target; t++) session.simulate(t, true, false);
		session.presented = world.tick;
		session.hashedThrough =
			Math.floor(session.confirmedTick / options.hashInterval) * options.hashInterval;
		session.hooks = hooks;
		return session;
	}

	/** The (first) other player; in a duel the only one. */
	get remote(): PlayerSlot {
		return this.remotes[0] ?? 1;
	}

	/** Ticks for which every remote input is known (ticks 0 .. n-1 are certain). */
	get remoteTicks(): number {
		let min = Number.POSITIVE_INFINITY;
		for (const slot of this.remotes) min = Math.min(min, this.inputs[slot]?.length ?? 0);
		return min === Number.POSITIVE_INFINITY ? 0 : min;
	}

	/** Ticks simulated ahead of the remote inputs (negative: the remote player is ahead). */
	get prediction(): number {
		return this.world.tick - this.remoteTicks;
	}

	/** World ticks computed from real inputs only (they can't change any more). */
	get confirmedTick(): number {
		return Math.min(this.world.tick, this.remoteTicks);
	}

	/** True while the session is at its prediction limit and waits for the remote player. */
	get stalled(): boolean {
		return this.prediction >= this.options.maxPrediction;
	}

	/**
	 * Takes remote inputs for ticks `start ..` (overlap with known ticks is ignored; a gap is
	 * an error). Mispredictions are fixed at the next `advance()`.
	 */
	receiveRemote(
		start: number,
		bits: readonly number[],
		advantage?: number,
		slot: PlayerSlot = this.remote,
	): void {
		const log = this.inputs[slot];
		if (!log || slot === this.local) throw new Error(`not a remote slot: ${slot}`);
		if (start > log.length) throw new Error(`remote input gap: have ${log.length}, got ${start}`);
		for (let i = log.length - start; i < bits.length; i++) {
			const t = start + i;
			const b = bits[i] as number;
			log.push(b);
			if (t < this.world.tick && this.used[t]?.[slot] !== b && t < this.mispredicted) {
				this.mispredicted = t;
			}
		}
		if (advantage !== undefined) this.advantages.set(slot, advantage);
	}

	/**
	 * One fixed step of wall-clock time: fixes mispredictions, then simulates the next tick
	 * with `local` as this player's input (applied `inputDelay` ticks later). Returns false
	 * when it waits instead: at the prediction limit, or slowing down for the time sync.
	 */
	advance(local: Readonly<ControlState>): boolean {
		this.rollbackIfNeeded();
		this.reportHashes();
		this.pruneSnapshots();
		if (this.stalled || this.syncWait > 0) {
			if (this.syncWait > 0 && !this.stalled) this.syncWait--;
			this.stats.waits++;
			return false;
		}
		const t = this.world.tick;
		this.inputs[this.local]?.push(packControls(local));
		this.simulate(t, false, false);
		this.presented = Math.max(this.presented, this.world.tick);
		this.reportHashes();
		if (t % this.options.syncInterval === 0) this.timeSync();
		return true;
	}

	/** Local inputs not sent yet, plus the time-sync advantage, or null if there are none. */
	takeOutgoing(): { start: number; bits: number[]; adv: number } | null {
		const log = this.inputs[this.local] ?? [];
		if (this.sent >= log.length) return null;
		const start = this.sent;
		const bits = log.slice(start, start + MAX_INPUTS_PER_MESSAGE);
		this.sent += bits.length;
		return { start, bits, adv: this.prediction };
	}

	/** The confirmed world at a recently hashed tick (desync reports), if still kept. */
	stateAt(tick: number): WorldState | null {
		return this.hashedStates.get(tick) ?? null;
	}

	/** A player's inputs so far, by tick (replays, tests). */
	inputLog(slot: PlayerSlot): readonly number[] {
		return this.inputs[slot] ?? [];
	}

	/** Hashes of final (fully confirmed) states to report for desync detection. */
	takeHashes(): Array<{ tick: number; hash: string }> {
		return this.pendingHashes.splice(0);
	}

	private rollbackIfNeeded(): void {
		const from = this.mispredicted;
		this.mispredicted = Number.POSITIVE_INFINITY;
		if (from >= this.world.tick) return;
		const snapshot = this.snapshots.get(from);
		if (!snapshot) throw new Error(`no snapshot for tick ${from}`);
		const to = this.world.tick;
		this.hooks.beforeRollback?.(this.world);
		Object.assign(this.world, structuredClone(snapshot));
		// Later snapshots belong to the mispredicted timeline; the replay takes fresh ones.
		for (const t of this.snapshots.keys()) if (t > from) this.snapshots.delete(t);
		for (let t = from; t < to; t++) this.simulate(t, t < this.presented, t > from);
		const ticks = to - from;
		this.stats.rollbacks++;
		this.stats.resimulated += ticks;
		this.stats.longestRollback = Math.max(this.stats.longestRollback, ticks);
		this.hooks.afterRollback?.(from, ticks);
	}

	/**
	 * Steps tick t. The state before it is kept while it may still be rolled back (remote input
	 * predicted) and, during a replay, always: confirmed hashes are read from those snapshots.
	 */
	private simulate(t: number, replay: boolean, keepSnapshot: boolean): void {
		// Each remote input: the real one if known, else "same as their last known".
		const used = this.used[t] ?? [];
		this.used[t] = used;
		let allKnown = true;
		for (const slot of this.remotes) {
			const log = this.inputs[slot] ?? [];
			const known = t < log.length;
			if (!known) allKnown = false;
			used[slot] = known ? (log[t] as number) : (log.at(-1) ?? 0);
			this.controls[slot] = unpackControls(used[slot] as number);
		}
		if (!allKnown || keepSnapshot) this.snapshots.set(t, structuredClone(this.world));
		this.controls[this.local] = unpackControls(this.inputs[this.local]?.[t] ?? 0);
		this.hooks.beforeStep?.(this.world);
		const events = step(this.world, this.controls);
		this.hooks.afterStep?.(events, t, replay);
	}

	/** Hashes every hash tick that became final: the current world or a snapshot of it. */
	private reportHashes(): void {
		const interval = this.options.hashInterval;
		for (let h = this.hashedThrough + interval; h <= this.confirmedTick; h += interval) {
			const state = h === this.world.tick ? this.world : this.snapshots.get(h);
			if (state) {
				this.pendingHashes.push({ tick: h, hash: hashWorld(state) });
				this.hashedStates.set(h, structuredClone(state));
				for (const t of this.hashedStates.keys())
					if (t < h - KEPT_HASHED_TICKS) this.hashedStates.delete(t);
			}
			this.hashedThrough = h; // final now: reported, or (shouldn't happen) skipped for good
		}
	}

	private pruneSnapshots(): void {
		const confirmed = this.remoteTicks;
		for (const t of this.snapshots.keys()) if (t < confirmed) this.snapshots.delete(t);
	}

	/**
	 * Every player computes "ticks ahead of the slowest inputs it has". Latency adds the same to
	 * both sides of a pair, so half the difference to the slowest remote player's own figure is
	 * how far this side runs ahead of it: wait that long (everyone syncs to the slowest).
	 */
	private timeSync(): void {
		let slowest = this.remote;
		for (const slot of this.remotes) {
			if ((this.inputs[slot]?.length ?? 0) < (this.inputs[slowest]?.length ?? 0)) slowest = slot;
		}
		const lead = Math.floor((this.prediction - (this.advantages.get(slowest) ?? 0)) / 2);
		this.syncWait = Math.min(MAX_SYNC_WAIT, Math.max(0, lead));
	}
}
